import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';


const BankIntegrationStep = ({ data, organizationInfo, onComplete, onBack, onCancel }) => {
  const [formData, setFormData] = useState({
    selectedBanks: data?.selectedBanks || [],
    chartOfAccounts: data?.chartOfAccounts || {},
    payrollRouting: data?.payrollRouting || '',
    nasfundIntegration: data?.nasfundIntegration || false,
    employeesImported: data?.employeesImported || false
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const pngBanks = [
    { 
      id: 'bsp', 
      name: 'Bank South Pacific (BSP)', 
      code: 'BSP',
      logo: '🏦',
      apiEndpoint: 'https://api.bsp.com.pg',
      description: 'PNG\'s largest bank with comprehensive payroll services'
    },
    { 
      id: 'anz', 
      name: 'ANZ PNG', 
      code: 'ANZ',
      logo: '🏦',
      apiEndpoint: 'https://api.anz.com.pg',
      description: 'International banking with local expertise'
    },
    { 
      id: 'westpac', 
      name: 'Westpac PNG', 
      code: 'WPAC',
      logo: '🏦',
      apiEndpoint: 'https://api.westpac.com.pg',
      description: 'Trusted banking partner for PNG businesses'
    },
    { 
      id: 'kinabank', 
      name: 'Kina Bank', 
      code: 'KINA',
      logo: '🏦',
      apiEndpoint: 'https://api.kinabank.com.pg',
      description: 'PNG\'s locally-owned commercial bank'
    }
  ];

  const [bankConfigs, setBankConfigs] = useState(
    pngBanks?.reduce((acc, bank) => {
      acc[bank.id] = {
        active: data?.selectedBanks?.includes(bank?.id) || false,
        apiKey: data?.bankConfigs?.[bank?.id]?.apiKey || '',
        accountNumber: data?.bankConfigs?.[bank?.id]?.accountNumber || ''
      };
      return acc;
    }, {})
  );

  const payrollRoutingOptions = [
    { value: 'direct_deposit', label: 'Direct Deposit (EFT)' },
    { value: 'bank_file', label: 'Bank File Generation' },
    { value: 'manual', label: 'Manual Processing' }
  ];

  const handleBankToggle = (bankId) => {
    setBankConfigs(prev => ({
      ...prev,
      [bankId]: {
        ...prev?.[bankId],
        active: !prev?.[bankId]?.active
      }
    }));
  };

  const handleBankConfigChange = (bankId, field, value) => {
    setBankConfigs(prev => ({
      ...prev,
      [bankId]: {
        ...prev?.[bankId],
        [field]: value
      }
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const activeBanks = Object.entries(bankConfigs)?.filter(([_, config]) => config?.active);

    if (activeBanks?.length === 0) {
      newErrors.banks = 'Please select at least one bank';
    }

    // Validate active bank configurations
    activeBanks?.forEach(([bankId, config]) => {
      if (!config?.accountNumber?.trim()) {
        newErrors[`${bankId}_account`] = 'Account number is required';
      }
    });

    if (!formData?.payrollRouting) {
      newErrors.payrollRouting = 'Payroll routing method is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        setSaving(true);
        const selectedBanks = Object.entries(bankConfigs)?.filter(([_, config]) => config?.active)?.map(([bankId]) => bankId);

        // Save bank integration settings (optional - can be saved to company_settings or separate table)
        // For now, just pass data forward
        
        onComplete?.({
          ...formData,
          selectedBanks,
          bankConfigs,
          employeesImported: true // Mark as true if employee import happens in this step
        });
      } catch (err) {
        console.error('Error saving bank integration:', err);
        setErrors({ submit: err?.message || 'Failed to save bank integration' });
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Icon name="CreditCard" size={24} className="text-emerald-600 mr-2" />
          <h2 className="text-2xl font-bold text-gray-900">Bank Integration</h2>
        </div>
        <p className="text-gray-600">
          Connect to PNG financial institutions with secure API integration and automated account verification.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Display */}
        {errors?.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{errors?.submit}</p>
            </div>
          </div>
        )}

        {/* Bank Selection */}
        <div>
          <label className="text-sm font-medium text-gray-900 mb-3 block">
            Select Banks to Integrate <span className="text-red-600">*</span>
          </label>
          {errors?.banks && (
            <p className="text-sm text-red-600 mb-2">{errors?.banks}</p>
          )}
          <div className="space-y-4">
            {pngBanks?.map(bank => (
              <div key={bank?.id} className="border border-gray-200 rounded-lg p-4 hover:border-emerald-300 transition-colors">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <Checkbox
                      checked={bankConfigs?.[bank?.id]?.active}
                      onChange={() => handleBankToggle(bank?.id)}
                    />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      <span className="text-2xl mr-2">{bank?.logo}</span>
                      <div>
                        <label className="font-semibold text-gray-900">{bank?.name}</label>
                        <p className="text-sm text-gray-600">{bank?.description}</p>
                      </div>
                    </div>
                    
                    {bankConfigs?.[bank?.id]?.active && (
                      <div className="mt-4 space-y-3 pl-8">
                        <Input
                          label="Account Number"
                          required
                          value={bankConfigs?.[bank?.id]?.accountNumber}
                          onChange={(e) => handleBankConfigChange(bank?.id, 'accountNumber', e?.target?.value)}
                          error={errors?.[`${bank?.id}_account`]}
                          placeholder="Enter account number"
                        />
                        <Input
                          label="API Key (Optional)"
                          value={bankConfigs?.[bank?.id]?.apiKey}
                          onChange={(e) => handleBankConfigChange(bank?.id, 'apiKey', e?.target?.value)}
                          placeholder="Enter API key for automated integration"
                          description="Leave blank for manual file generation"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payroll Routing */}
        <Select
          label="Payroll Routing Configuration"
          required
          options={payrollRoutingOptions}
          value={formData?.payrollRouting}
          onChange={(value) => handleChange('payrollRouting', value)}
          error={errors?.payrollRouting}
          placeholder="Select payroll routing method"
        />

        {/* NASFUND Integration */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex items-center h-5 mt-1">
              <Checkbox
                checked={formData?.nasfundIntegration}
                onChange={(e) => handleChange('nasfundIntegration', e?.target?.checked)}
              />
            </div>
            <div className="ml-3">
              <label className="font-semibold text-gray-900">Enable NASFUND Integration</label>
              <p className="text-sm text-gray-700 mt-1">
                Automatically calculate and submit NASFUND contributions with real-time validation.
              </p>
            </div>
          </div>
        </div>

        {/* Chart of Accounts Info */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-emerald-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">Chart of Accounts Mapping</p>
              <p className="text-sm text-emerald-700 mt-1">
                We'll automatically map your payroll accounts to the selected banks. You can customize mappings later in the system settings.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              iconName="ArrowLeft"
              disabled={saving}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              iconName="X"
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
          <Button
            type="submit"
            iconName="ArrowRight"
            iconPosition="right"
            loading={saving}
          >
            Continue to Payroll Rules
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BankIntegrationStep;